import React from "react";
import {Box, Typography, Stack, Skeleton, IconButton, Switch} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {helper} from "../../../utils/helper.js";
import {laveuseHelper} from "../../../utils/laveuseHelper.js";
import Button from "@mui/material/Button";
import {TravelIcon} from "../../../utils/ui/Icons/TravelIcon.js";
import {EngineOnIcon} from "../../../utils/ui/Icons/EngineOnIcon.js";
import {EngineOffIcon} from "../../../utils/ui/Icons/EngineOff.js";

const engineStateLabel = {
    on: "En route",
    off: "Arrêté",
    idle: "En pause"
};


function GaugeBar({value, color}) {
    return (
        <Box sx={{width: 75, height: 6, backgroundColor: '#e2e8f0', borderRadius: '3px', overflow: 'hidden'}}>
            <Box sx={{height: '100%', width: `${Math.min(value || 0, 100)}%`, backgroundColor: color, borderRadius: '3px'}}/>
        </Box>
    );
}

const engineStateColor = {
    on: "#5cb85c",
    off: "#a0aec0",
    idle: "#f6c94e"
};

function DetailRow({icon, label, children, labelColor}) {
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center"
               sx={{py: 1, borderBottom: '1px solid #e2e8f0', '&:last-child': {borderBottom: 'none'}}}>
            {(label || icon) &&
              <Typography sx={{color: labelColor || '#718096', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px'}}>
                  {icon} {label}
              </Typography>
            }
            <Box sx={{width: (!label && !icon) ? '100%' : 'auto'}}>
                {children}
            </Box>
        </Stack>
    );
}

export default function LaveuseDetailPanel({laveuse, telematics, onClose, onShowTrips, showRoute, onToggleRoute, context, showTournee, onToggleTournee}) {
    if (!laveuse) return null;

    const t = telematics?.[laveuse.id];
    const live = t?.live;
    const route = t?.route;
    const loaded = live != null;
    const engine = live?.engineState ?? laveuse?.live?.engineState;

    const defLevel = live?.defLevel;
    const defColor = defLevel < 20 ? '#e74c3c' : defLevel < 40 ? '#f39c12' : '#38b2ac';

    const fuelPercent = live?.fuelPercent;
    const engineRpm = live?.engineRpm;
    const fuelColor = fuelPercent < 20 ? '#e74c3c' : fuelPercent < 40 ? '#f39c12' : '#276749';

    const odometerKm = live?.odometer != null ? (live.odometer / 1000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ") : null;

    // Compute engineTimes à partir de la route
    const engineTimes = laveuseHelper.getEngineTimes(route);
    const ignitionOn = engineTimes.ignitionOn;
    const ignitionOff = engineTimes.ignitionOff;

    const hasRoute = route && route.length > 0;

    const tourneeContext = context?.context;
    const hasTournee = tourneeContext?.tournees?.length > 0;
    const stats = tourneeContext?.stats;

    return (
        <Box sx={{
            position: 'absolute', bottom: 20, right: 20, width: 300,
            backgroundColor: 'white', borderRadius: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,.15)',
            overflow: 'hidden', zIndex: 1000,
            border: '1px solid #e2e8f0'
        }}>
            {/* Header */}
            <Box sx={{backgroundColor: '#1e3a5f', padding: '13px 15px', display: 'flex', alignItems: 'center', gap: '10px'}}>
                <Box
                    component="img"
                    src="/images/mini_laveuse.webp"
                    alt=""
                    sx={{width: 56, height: 36, objectFit: 'contain'}}
                    onError={(e) => { e.target.style.display = 'none'; }}
                />
                <Box sx={{flex: 1}}>
                    <Typography sx={{color: 'white', fontSize: '15px', fontWeight: 700}}>
                        {laveuse.description || laveuse.immatriculation}
                    </Typography>
                    <Typography sx={{color: 'rgba(255,255,255,.55)', fontSize: '12px'}}>
                        👤 {laveuse.operateur?.libelle || "Non affecté"}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose}
                            sx={{backgroundColor: 'rgba(255,255,255,.15)', color: 'white', width: 26, height: 26}}>
                    <CloseIcon sx={{fontSize: 14}}/>
                </IconButton>
            </Box>

            {/* Body */}
            <Box sx={{padding: '13px 15px'}}>
                {/* État */}
                <DetailRow icon="" label="État">
                    {loaded ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={{
                                width: 8, height: 8, borderRadius: '50%',
                                backgroundColor: engineStateColor[engine] || '#a0aec0',
                                boxShadow: engine === 'on' ? '0 0 0 3px rgba(92,184,92,.25)' : 'none',
                                animation: engine === 'on' ? 'pulse 2s infinite' : 'none',
                                '@keyframes pulse': {
                                    '0%,100%': {opacity: 1, transform: 'scale(1)'},
                                    '50%': {opacity: 0.5, transform: 'scale(1.3)'}
                                }
                            }}/>
                            <Typography sx={{fontSize: '13px', fontWeight: 600, color: '#1e3a5f'}}>
                                {engineStateLabel[engine] || "—"}
                            </Typography>
                        </Stack>
                    ) : <Skeleton width={70} height={24}/>}
                </DetailRow>

                {/* Adresse */}
                <DetailRow label="Adresse">
                    {loaded ? (
                        <Typography sx={{fontSize: '11px', fontWeight: 600, textAlign: 'right', maxWidth: 155, color: '#718096'}}>
                            📍 {live?.address || "—"}
                        </Typography>
                    ) : <Skeleton width={120} height={16}/>}
                </DetailRow>

                {/* AdBlue / DEF */}
                <DetailRow icon="💧" label="AdBlue">
                    {loaded ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <GaugeBar value={defLevel} color={defColor}/>
                            <Typography sx={{
                                fontSize: '13px', fontWeight: 600,
                                color: defLevel != null ? defColor : '#718096'
                            }}>
                                {defLevel != null ? `${defLevel.toFixed(0)}%` : "—"}
                            </Typography>
                        </Stack>
                    ) : <Skeleton width={100} height={16}/>}
                </DetailRow>

                {/* Carburant / RPM */}
                <DetailRow icon={fuelPercent != null ? "⛽" : "🔧"} label={fuelPercent != null ? "Carburant" : "Régime moteur"}>
                    {loaded ? (
                      <Stack direction="row" alignItems="center" spacing={1}>
                          {fuelPercent != null && <GaugeBar value={fuelPercent} color={fuelColor}/>}
                          <Typography sx={{
                              fontSize: '13px', fontWeight: 600,
                              color: fuelPercent != null ? fuelColor : engineRpm != null ? '#2b6cb0' : '#718096'
                          }}>
                              {fuelPercent != null ? `${fuelPercent.toFixed(0)}%` : engineRpm != null ? `${engineRpm} rpm` : "—"}
                          </Typography>
                      </Stack>
                    ) : <Skeleton width={60} height={16}/>}
                </DetailRow>

                {/* Kilométrage */}
                <DetailRow label="Kilométrage">
                    {loaded ? (
                        <Typography sx={{fontSize: '13px', fontWeight: 600}}>
                            {odometerKm != null ? `${odometerKm} km` : "—"}
                        </Typography>
                    ) : <Skeleton width={80} height={16}/>}
                </DetailRow>

                {/* Vitesse */}
                <DetailRow label="Vitesse">
                    {loaded ? (
                        <Typography sx={{fontSize: '13px', fontWeight: 600}}>
                            {live?.speed != null ? `${live.speed} km/h` : "—"}
                        </Typography>
                    ) : <Skeleton width={60} height={16}/>}
                </DetailRow>

                {/* Dernière MAJ */}
                <DetailRow icon="🕐" label="Dernière MAJ">
                    {loaded ? (
                        <Typography sx={{fontSize: '11px', fontWeight: 600}}>
                            {helper.formatDate(live?.lastUpdate) + " " + helper.getFormattedTime(live?.lastUpdate, false)  || "—"}
                        </Typography>
                    ) : <Skeleton width={100} height={16}/>}
                </DetailRow>

                {/* Engine times inline */}
                <DetailRow>
                    {loaded ? (
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{width: '100%'}}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                              <EngineOnIcon style={{fontSize: "18px"}}/>
                              <Typography sx={{fontSize: '13px', fontWeight: 600}}>
                                  {ignitionOn ? helper.getFormattedTime(ignitionOn, false) : "—"}
                              </Typography>
                          </Stack>
                          <Box sx={{flex: 1, height: '1px', backgroundColor: '#cbd5e0', mx: 2}}/>
                          <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography sx={{fontSize: '13px', fontWeight: 600}}>
                                  {ignitionOff ? helper.getFormattedTime(ignitionOff, false) : "—"}
                              </Typography>
                              <EngineOffIcon style={{fontSize: "18px"}}/>
                          </Stack>
                      </Stack>
                    ) : <Skeleton width={120} height={16}/>}
                </DetailRow>

                {/* Codes défaut */}
                {loaded && Array.isArray(live?.faultCodes) && live.faultCodes.length > 0 && (
                    <DetailRow icon="⚠️" label="Défaut(s)" labelColor="#e74c3c">
                        <Box sx={{textAlign: 'right', maxWidth: 160}}>
                            {live.faultCodes.map((code, i) => (
                                <Typography key={i} sx={{fontSize: '11px', fontWeight: 600, color: '#e74c3c'}}>
                                    {code}
                                </Typography>
                            ))}
                        </Box>
                    </DetailRow>
                )}

                {/* Toggle Afficher route */}
                {hasRoute && onToggleRoute && (
                    <DetailRow icon="🗺️" label="Afficher route">
                        <Switch
                            size="small"
                            checked={!!showRoute}
                            onChange={(e) => onToggleRoute(e.target.checked)}
                        />
                    </DetailRow>
                )}

                {/* Toggle Afficher tournée */}
                {hasTournee && onToggleTournee && (
                    <DetailRow icon="📍" label="Afficher tournée">
                        <Switch
                            size="small"
                            checked={!!showTournee}
                            onChange={(e) => onToggleTournee(e.target.checked)}
                        />
                    </DetailRow>
                )}

                {/* Stats tournée */}
                {showTournee && stats && (
                    <Box sx={{mt: 1, p: 1.5, backgroundColor: '#f7f9fc', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 0.5}}>
                            <Typography sx={{fontSize: '12px', fontWeight: 700, color: '#1e3a5f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160}}>
                                {tourneeContext.operateur?.libelle ?? '—'}
                            </Typography>
                            <Typography sx={{fontSize: '12px', fontWeight: 700, color: '#1e3a5f'}}>
                                {stats.progressionPct?.toFixed(1)}%
                            </Typography>
                        </Stack>
                        <Box sx={{width: '100%', height: 5, backgroundColor: '#e2e8f0', borderRadius: '3px', mb: 1, overflow: 'hidden'}}>
                            <Box sx={{height: '100%', width: `${Math.min(stats.progressionPct ?? 0, 100)}%`, backgroundColor: '#4CAF50', borderRadius: '3px'}}/>
                        </Box>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography sx={{fontSize: '11px', color: '#4CAF50', fontWeight: 600}}>✓ {stats.nombreFait}</Typography>
                            <Typography sx={{fontSize: '11px', color: '#F44336', fontWeight: 600}}>✗ {stats.nombreImpossible}</Typography>
                            <Typography sx={{fontSize: '11px', color: '#FF9800', fontWeight: 600}}>⚠ {stats.nombreMaintenance}</Typography>
                            <Typography sx={{fontSize: '11px', color: '#9E9E9E', fontWeight: 600}}>○ {stats.nombreTodo}</Typography>
                        </Stack>
                    </Box>
                )}

                {/* Bouton Trajets */}
                {onShowTrips && (
                    <Box sx={{mt: 1.5}}>
                        <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            startIcon={<TravelIcon fontSize={18}/>}
                            onClick={() => onShowTrips(laveuse)}
                        >
                            Historique trajet
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
